import { Box, HStack, Stack, Text, Button } from '@chakra-ui/react';

import { useInsightActions } from '@util/hooks/insight.hooks';
import { useInfiniteInsights } from '@services/query-services/insights.query-service';
import { HIDE_SCROLLBAR } from '@styles/css-configs';
import CenteredLayout from '@layouts/centered.layout';
import FeedScrollContainer from '@components/feed/feed-scroll-container.component';
import InsightCard from '@components/general/card/insight-card/insight-card.component';
import SummaryCardSkeleton from '@components/general/card/summary-card/summary-card-skeleton.component';
import StyledSelect from '@components/general/styled-select.component';
import { useMemo, useState } from 'react';
import { useUserStore } from '@store/app.store';
import { Link } from 'react-router-dom';

const FILTER_OPTIONS = [
  {
    value: 'default',
    label: 'Trending'
  },
  {
    value: 'shared',
    label: 'Shared with Me'
  }
];

export default function InsightsPage() {
  const user = useUserStore((state) => state.user);
  const [filter, setFilter] = useState(FILTER_OPTIONS[0].value);

  const insightsSearchBody = useMemo(
    () => ({
      sort: 'updated_at',
      asc: false,
      pageSize: 8,
      initiative_type: 'Lens',
      has_archived_at: false,
      ...(filter === 'shared' || !!user?.isGuest
        ? { shared_with_me: true }
        : { person_id: user?.id })
    }),
    [filter, user?.id, user?.isGuest]
  );

  const infiniteInsightsResult = useInfiniteInsights(insightsSearchBody, {
    refetchOnMount: false
  });

  const onInsightActionClick = useInsightActions({
    type: 'infinite',
    queryKeyToUpdate: insightsSearchBody
  });

  return (
    <CenteredLayout>
      <Box p={11}>
        <HStack mb={11}>
          <Text
            variant='page-header'
            data-testid='page-header'
          >
            Insights For You
          </Text>

          {!user?.isGuest && (
            <StyledSelect
              options={FILTER_OPTIONS}
              value={filter}
              onChange={setFilter}
              variant='reskin'
            />
          )}
          <Link to='/lenses'>
            <Button
              variant='reskinPrimary'
              size='sm'
            >
              View Lenses
            </Button>
          </Link>
        </HStack>
        <Stack gap={8}>
          <FeedScrollContainer
            infiniteQueryResult={infiniteInsightsResult}
            noResultsProps={{ mt: 6 }}
            loadingSkeleton={<SummaryCardSkeleton />}
            gap={{ base: 6, md: 11 }}
            overflowY={{ base: 'unset', xl: 'scroll' }}
            sx={HIDE_SCROLLBAR}
            data-id='insights-feed'
          >
            {(page, pageIndex) =>
              (page?.data || []).map((insight, insightIndex) => (
                <InsightCard
                  key={insight?.id}
                  insight={insight}
                  onInsightActionClick={(action, value, insight) =>
                    onInsightActionClick(action, value, insight, pageIndex, insightIndex)
                  }
                />
              ))
            }
          </FeedScrollContainer>
        </Stack>
      </Box>
    </CenteredLayout>
  );
}
